import React, { useState, useEffect } from 'react';
import { jobsAPI, unwrapList } from '../services/api';
import Pagination, { paginateItems } from './Pagination';

function ActiveJobs() {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Fetch jobs when component mounts
  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await jobsAPI.getJobs();
      setJobs(unwrapList(response.data));
    } catch (err) {
      setError('Failed to fetch jobs');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Filter jobs based on search term
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = 
      (job.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
      (job.salon_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.location || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const { pageItems, total, currentPage: safePage } = paginateItems(filteredJobs, currentPage);

  // Handle job selection for details view
  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-semibold mb-6">Active Jobs</h1>
      
      {/* Search */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div>
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <input
            type="text"
            id="search"
            className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600"
            placeholder="Search by job title, company, or location"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {/* Jobs table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Posted Date</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salary Range</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageItems.length > 0 ? (
                pageItems.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-medium">{(job.title || 'J').charAt(0)}</span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{job.title || 'No title'}</div>
                          <div className="text-sm text-gray-500">{job.salon_name || 'No salon name'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.location || 'Not specified'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.posted_date ? new Date(job.posted_date).toLocaleDateString() : 'Not available'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.pay_min && job.pay_max ? `₹${job.pay_min} - ₹${job.pay_max}` : 'Not specified'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => handleViewDetails(job)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No jobs found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={safePage} total={total} onPageChange={setCurrentPage} />
      
      {/* Job Details Modal */}
      {isModalOpen && selectedJob && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeModal}></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl leading-6 font-bold text-gray-900">
                        Job Details
                      </h3>
                      <button 
                        onClick={closeModal}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-1">{selectedJob.title}</h2>
                      <div className="text-sm text-gray-500 mb-2">
                        {selectedJob.salon_name} • {selectedJob.location}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4 mt-4">
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>Posted: {selectedJob.posted_date ? new Date(selectedJob.posted_date).toLocaleDateString() : 'Not available'}</span>
                        </div>
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>Owner: {selectedJob.salon_owner_name || 'Not specified'}</span>
                        </div>
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>Job Type: {selectedJob.job_type ? selectedJob.job_type.replace('_', ' ') : 'Not specified'}</span>
                        </div>
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>Email: {selectedJob.salon_email || 'Not specified'}</span>
                        </div>
                      </div>
                    </div>
                    
                    {selectedJob.job_summary && (
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-gray-800 mb-2">Job Summary</h4>
                        <p className="text-gray-600">{selectedJob.job_summary}</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Key Responsibilities Section */}
                      {selectedJob.key_responsibilities && selectedJob.key_responsibilities.length > 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                          <h4 className="text-lg font-semibold text-gray-800 mb-3">Key Responsibilities</h4>
                          <ul className="list-disc pl-5 space-y-2">
                            {selectedJob.key_responsibilities.map((responsibility, index) => (
                              <li key={index} className="text-gray-600">{responsibility}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Benefits Section */}
                      {selectedJob.benefits && selectedJob.benefits.length > 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                          <h4 className="text-lg font-semibold text-gray-800 mb-3">Benefits</h4>
                          <ul className="list-disc pl-5 space-y-2">
                            {selectedJob.benefits.map((benefit, index) => (
                              <li key={index} className="text-gray-600">{benefit}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    {/* Additional Information */}
                    <div className="mt-6">
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h4 className="text-lg font-semibold text-gray-800 mb-4">Additional Information</h4>
                        
                        <div className="space-y-4">
                          {selectedJob.experience && (
                            <div>
                              <h5 className="font-medium text-gray-700 mb-1">Experience Required</h5>
                              <p className="text-gray-600">{selectedJob.experience}</p>
                            </div>
                          )}
                          
                          {selectedJob.gender && (
                            <div>
                              <h5 className="font-medium text-gray-700 mb-1">Gender Preference</h5>
                              <p className="text-gray-600 capitalize">{selectedJob.gender}</p>
                            </div>
                          )}
                          
                          {selectedJob.salon_contact_no && (
                            <div>
                              <h5 className="font-medium text-gray-700 mb-1">Salon Contact</h5>
                              <p className="text-gray-600">{selectedJob.salon_contact_no}</p>
                            </div>
                          )}
                          
                          {selectedJob.salon_pin_code && (
                            <div>
                              <h5 className="font-medium text-gray-700 mb-1">PIN Code</h5>
                              <p className="text-gray-600">{selectedJob.salon_pin_code}</p>
                            </div>
                          )}
                          
                          {selectedJob.salon_type && (
                            <div>
                              <h5 className="font-medium text-gray-700 mb-1">Salon Type</h5>
                              <p className="text-gray-600">{selectedJob.salon_type}</p>
                            </div>
                          )}
                          
                          {selectedJob.salon_google_map_link && (
                            <div>
                              <h5 className="font-medium text-gray-700 mb-1">Location</h5>
                              <a 
                                href={selectedJob.salon_google_map_link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline"
                              >
                                View on Google Maps
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActiveJobs;
